import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

export interface QuoteChoiceDecision {
  bookingServiceId: string;
  decision: 'accepted' | 'rejected';
}

export interface QuoteChoiceJobRow {
  bookingServiceId: string;
  customerRequired: boolean;
  name: string;
  grossCents: number | null;
}

/** Latest customer answer on the quote, plus each live job. */
export interface QuoteChoiceView {
  response: string | null;
  decisions: QuoteChoiceDecision[] | null;
  jobs: QuoteChoiceJobRow[];
}

export function useQuoteJobChoice(workOrderId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.workOrders.detail(workOrderId ?? ''), 'quote-choice'],
    queryFn: () => api.get<QuoteChoiceView>(`/work-orders/${workOrderId}/quote-choice`),
    enabled: Boolean(workOrderId),
  });
}

export function useSetCustomerRequired() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      workOrderId: string;
      serviceId: string;
      required: boolean;
      expectedUpdatedAt: string;
    }) => api.patch<{ customerRequired: boolean; updatedAt: string }>(
      `/work-orders/${input.workOrderId}/services/${input.serviceId}/customer-required`,
      { required: input.required },
      { headers: { 'If-Match': input.expectedUpdatedAt } },
    ),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(input.workOrderId) });
    },
  });
}
